import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ClinicalPageShell } from "@/components/clinical";
import { CrudEmptyState, CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useWorkspaceLocationId } from "@/lib/location-context";
import { Loader2, Plus, Trash2, Check, ChevronsUpDown } from "lucide-react";
import { TRANSFER_STATUS_LABELS, allowedTransferTransitions } from "@/domain/sm-models";
import type { TransferStatus } from "@/domain/sm-models";

interface Location {
  id: string;
  name: string;
  active: boolean;
}

interface ProductOpt {
  id: string;
  name: string;
}

interface TransferSummary {
  id: string;
  fromLocationId: string;
  toLocationId: string;
  status: TransferStatus;
  notes: string | null;
  createdAt: string;
}

interface TransferItem {
  id: string;
  productId: string;
  productName?: string;
  qtyRequested: number;
  qtySent: number | null;
  qtyReceived: number | null;
}

interface TransferDetail {
  transfer: TransferSummary;
  items: TransferItem[];
}

const STATUS_TONE: Record<TransferStatus, "secondary" | "outline" | "default" | "destructive"> = {
  SOLICITADA: "secondary",
  APROBADA: "outline",
  EN_TRANSITO: "default",
  RECIBIDA: "default",
  RECHAZADA: "destructive",
  CANCELADA: "destructive",
};

function locationName(locs: Location[], id: string): string {
  return locs.find((l) => l.id === id)?.name ?? id;
}

export const Route = createFileRoute("/app/transferencias")({
  component: TransferenciasPage,
  head: () => ({ meta: [{ title: "Transferencias — Structura Market" }] }),
});

function TransferenciasPage() {
  const tid = useWorkspaceTenantId();
  const [locs, setLocs] = useState<Location[]>([]);
  const [products, setProducts] = useState<ProductOpt[]>([]);
  const [transfers, setTransfers] = useState<TransferSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<TransferDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [receiveQty, setReceiveQty] = useState<Record<string, string>>({});

  const load = () => {
    if (!tid) return;
    setLoading(true);
    Promise.all([
      apiGetJson<Location[]>("/api/locations", tid),
      apiGetJson<{ items: ProductOpt[] } | ProductOpt[]>("/api/products", tid),
      apiGetJson<{ transfers: TransferSummary[] }>("/api/stock-transfers", tid),
    ])
      .then(([locsRes, prodRes, trRes]) => {
        setLocs(locsRes.filter((l) => l.active));
        setProducts(Array.isArray(prodRes) ? prodRes : prodRes.items);
        setTransfers(trRes.transfers);
      })
      .catch(() => toast.error("Error al cargar transferencias"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [tid]);

  const openDetail = (id: string) => {
    if (!tid) return;
    setSelectedId(id);
    setDetailLoading(true);
    apiGetJson<TransferDetail>(`/api/stock-transfers?id=${id}`, tid)
      .then((d) => {
        setDetail(d);
        const qty: Record<string, string> = {};
        for (const item of d.items) qty[item.productId] = String(item.qtySent ?? item.qtyRequested);
        setReceiveQty(qty);
      })
      .catch(() => toast.error("Error al cargar el detalle"))
      .finally(() => setDetailLoading(false));
  };

  const runAction = async (action: "approve" | "reject" | "cancel" | "send" | "receive") => {
    if (!tid || !selectedId || !detail) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = { action };
      if (action === "receive") {
        body.items = detail.items.map((i) => ({
          productId: i.productId,
          qtyReceived: Number(receiveQty[i.productId] ?? 0),
        }));
      }
      await apiSendJson(`/api/stock-transfers?id=${selectedId}`, "PATCH", body, tid);
      toast.success("Transferencia actualizada");
      load();
      openDetail(selectedId);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo actualizar la transferencia");
    } finally {
      setSaving(false);
    }
  };

  const allowed = detail ? allowedTransferTransitions(detail.transfer.status) : [];

  return (
    <ClinicalPageShell className="mx-auto w-full max-w-[1400px] space-y-4">
      <CrudShellHeader
        title="Transferencias entre sucursales"
        subtitle="Mové stock de una sucursal a otra con trazabilidad completa"
        actions={
          <Button onClick={() => setCreateOpen(true)} disabled={locs.length < 2} className="gap-2">
            <Plus className="h-4 w-4" /> Nueva transferencia
          </Button>
        }
      />

      {loading ? (
        <Loading />
      ) : locs.length < 2 ? (
        <CrudEmptyState
          title="Necesitás al menos 2 sucursales activas"
          description="Las transferencias mueven stock entre sucursales del mismo comercio. Agregá una segunda sucursal en /app/sucursales para empezar a usarlas."
        />
      ) : transfers.length === 0 ? (
        <CrudEmptyState
          title="Todavía no hay transferencias"
          description="Creá la primera para mover stock entre sucursales."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[380px_1fr] gap-4">
          <div className="space-y-2">
            {transfers.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => openDetail(t.id)}
                className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                  selectedId === t.id ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">
                    {locationName(locs, t.fromLocationId)} → {locationName(locs, t.toLocationId)}
                  </span>
                  <Badge variant={STATUS_TONE[t.status]}>{TRANSFER_STATUS_LABELS[t.status]}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(t.createdAt).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false })}
                </p>
              </button>
            ))}
          </div>

          <div className="rounded-lg border p-4">
            {!detail ? (
              <p className="text-sm text-muted-foreground">Elegí una transferencia de la lista.</p>
            ) : detailLoading ? (
              <Loading />
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">
                      {locationName(locs, detail.transfer.fromLocationId)} → {locationName(locs, detail.transfer.toLocationId)}
                    </p>
                    <p className="text-xs text-muted-foreground">#{detail.transfer.id.slice(-8).toUpperCase()}</p>
                  </div>
                  <Badge variant={STATUS_TONE[detail.transfer.status]}>{TRANSFER_STATUS_LABELS[detail.transfer.status]}</Badge>
                </div>

                {detail.transfer.notes && (
                  <p className="text-sm italic text-muted-foreground rounded-md bg-muted/50 px-3 py-2">{detail.transfer.notes}</p>
                )}

                <div className="space-y-2">
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Ítems</p>
                  {detail.items.map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-2 text-sm border-b pb-2 last:border-0">
                      <span>{item.productName ?? item.productId}</span>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
                        <span>Pedido: {item.qtyRequested}</span>
                        {item.qtySent != null && <span>Enviado: {item.qtySent}</span>}
                        {detail.transfer.status === "EN_TRANSITO" ? (
                          <Input
                            type="number"
                            min={0}
                            step="0.001"
                            className="h-7 w-20"
                            value={receiveQty[item.productId] ?? ""}
                            onChange={(e) => setReceiveQty((prev) => ({ ...prev, [item.productId]: e.target.value }))}
                          />
                        ) : (
                          item.qtyReceived != null && <span>Recibido: {item.qtyReceived}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {allowed.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {allowed.includes("APROBADA") && (
                      <Button size="sm" variant="outline" disabled={saving} onClick={() => runAction("approve")}>
                        Aprobar
                      </Button>
                    )}
                    {allowed.includes("EN_TRANSITO") && (
                      <Button size="sm" disabled={saving} onClick={() => runAction("send")}>
                        Marcar enviada
                      </Button>
                    )}
                    {allowed.includes("RECIBIDA") && (
                      <Button size="sm" disabled={saving} onClick={() => runAction("receive")}>
                        Confirmar recepción
                      </Button>
                    )}
                    {allowed.includes("RECHAZADA") && (
                      <Button size="sm" variant="destructive" disabled={saving} onClick={() => runAction("reject")}>
                        Rechazar
                      </Button>
                    )}
                    {allowed.includes("CANCELADA") && (
                      <Button size="sm" variant="destructive" disabled={saving} onClick={() => runAction("cancel")}>
                        Cancelar
                      </Button>
                    )}
                    {saving && <Loader2 className="h-4 w-4 animate-spin self-center" />}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <CreateTransferDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        locs={locs}
        products={products}
        tid={tid}
        onCreated={() => {
          setCreateOpen(false);
          load();
        }}
      />
    </ClinicalPageShell>
  );
}

interface DraftItem {
  productId: string;
  /** SCRUM-196: opcional; requerido si el producto tiene variantes. */
  variantId?: string;
  qty: string;
}

interface VariantOpt {
  id: string;
  name: string;
}

function CreateTransferDialog({
  open,
  onOpenChange,
  locs,
  products,
  tid,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  locs: Location[];
  products: ProductOpt[];
  tid: string | null;
  onCreated: () => void;
}) {
  const contextLocationId = useWorkspaceLocationId();
  const [fromId, setFromId] = useState<string>("");
  const [toId, setToId] = useState<string>("");
  const [items, setItems] = useState<DraftItem[]>([{ productId: "", qty: "" }]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [openItemIndex, setOpenItemIndex] = useState<number | null>(null);
  // Stock disponible en `fromId` por producto — se muestra apenas se elige un
  // ítem, antes de que el usuario cargue cuánto transferir (evita pedir más
  // de lo que hay sin tener que adivinar o cancelar después por el 409).
  const [stockAtOrigin, setStockAtOrigin] = useState<Record<string, number>>({});
  const [stockLoading, setStockLoading] = useState(false);
  const [variantsByProduct, setVariantsByProduct] = useState<Record<string, VariantOpt[]>>({});

  useEffect(() => {
    if (open) {
      // SCRUM-72: origen default = sucursal activa del contexto (donde el
      // usuario está trabajando), no simplemente la primera de la lista.
      const defaultFrom = contextLocationId && locs.some((l) => l.id === contextLocationId)
        ? contextLocationId
        : (locs[0]?.id ?? "");
      setFromId(defaultFrom);
      setToId(locs.find((l) => l.id !== defaultFrom)?.id ?? "");
      setItems([{ productId: "", qty: "" }]);
      setNotes("");
      setVariantsByProduct({});
    }
  }, [open, locs, contextLocationId]);

  useEffect(() => {
    if (!open || !tid || !fromId || !products.length) return;
    setStockLoading(true);
    const productIds = products.map((p) => p.id).join(",");
    apiGetJson<{ stock: Record<string, number> }>(
      `/api/stock-by-location?locationId=${encodeURIComponent(fromId)}&productIds=${encodeURIComponent(productIds)}`,
      tid,
    )
      .then((r) => setStockAtOrigin(r.stock))
      .catch(() => setStockAtOrigin({}))
      .finally(() => setStockLoading(false));
  }, [open, tid, fromId, products]);

  const toOptions = useMemo(() => locs.filter((l) => l.id !== fromId), [locs, fromId]);

  const submit = async () => {
    if (!tid) return;
    if (!fromId || !toId) return toast.error("Elegí origen y destino");
    for (const i of items) {
      if (!i.productId || !(Number(i.qty) > 0)) continue;
      const variants = variantsByProduct[i.productId] ?? [];
      if (variants.length > 0 && !i.variantId) {
        return toast.error("Elegí la variante del producto con variantes");
      }
    }
    const validItems = items
      .filter((i) => i.productId && Number(i.qty) > 0)
      .map((i) => ({
        productId: i.productId,
        qtyRequested: Number(i.qty),
        ...(i.variantId ? { variantId: i.variantId } : {}),
      }));
    if (!validItems.length) return toast.error("Agregá al menos un ítem con cantidad");

    setSaving(true);
    try {
      await apiSendJson("/api/stock-transfers", "POST", {
        fromLocationId: fromId,
        toLocationId: toId,
        items: validItems,
        notes: notes || null,
      }, tid);
      toast.success("Transferencia solicitada");
      onCreated();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo crear la transferencia");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nueva transferencia</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Origen</Label>
              <Select value={fromId} onValueChange={setFromId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {locs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Destino</Label>
              <Select value={toId} onValueChange={setToId}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {toOptions.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Ítems</Label>
            {items.map((item, idx) => {
              const selectedProduct = products.find((p) => p.id === item.productId);
              const stock = item.productId ? stockAtOrigin[item.productId] : undefined;
              return (
                <div key={idx} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Popover open={openItemIndex === idx} onOpenChange={(o) => setOpenItemIndex(o ? idx : null)}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-expanded={openItemIndex === idx}
                          className="flex-1 justify-between font-normal"
                        >
                          <span className="truncate">{selectedProduct?.name ?? "Buscar producto…"}</span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Buscar producto…" className="h-9" />
                          <CommandList>
                            <CommandEmpty>Sin resultados</CommandEmpty>
                            <CommandGroup>
                              {products.map((p) => (
                                <CommandItem
                                  key={p.id}
                                  value={p.name}
                                  onSelect={() => {
                                    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, productId: p.id, variantId: undefined } : it)));
                                    setOpenItemIndex(null);
                                    if (tid && !variantsByProduct[p.id]) {
                                      void apiGetJson<VariantOpt[]>(`/api/products/variants?productId=${encodeURIComponent(p.id)}`, tid)
                                        .then((list) => setVariantsByProduct((prev) => ({ ...prev, [p.id]: Array.isArray(list) ? list : [] })))
                                        .catch(() => setVariantsByProduct((prev) => ({ ...prev, [p.id]: [] })));
                                    }
                                  }}
                                >
                                  <Check className={cn("mr-2 h-4 w-4", item.productId === p.id ? "opacity-100" : "opacity-0")} />
                                  <span className="flex-1 truncate">{p.name}</span>
                                  {stockAtOrigin[p.id] != null && (
                                    <span className="ml-2 text-[10px] text-muted-foreground tabular-nums">
                                      stock {stockAtOrigin[p.id]}
                                    </span>
                                  )}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <Input
                      type="number"
                      min={0}
                      step="0.001"
                      placeholder="Cant."
                      className="w-24"
                      value={item.qty}
                      onChange={(e) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, qty: e.target.value } : it)))}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      disabled={items.length === 1}
                      onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {item.productId && (
                    <p className="text-[11px] text-muted-foreground pl-1">
                      {stockLoading
                        ? "Consultando stock…"
                        : stock != null
                          ? `Stock disponible en origen: ${stock}`
                          : "Sin stock registrado en esta sucursal"}
                    </p>
                  )}
                  {(variantsByProduct[item.productId]?.length ?? 0) > 0 && (
                    <div className="pl-1">
                      <Label className="text-[11px]">Variante</Label>
                      <Select
                        value={item.variantId || undefined}
                        onValueChange={(v) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, variantId: v } : it)))}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue placeholder="Elegí variante…" />
                        </SelectTrigger>
                        <SelectContent>
                          {variantsByProduct[item.productId]!.map((v) => (
                            <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              );
            })}
            <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => setItems((prev) => [...prev, { productId: "", qty: "" }])}>
              <Plus className="h-4 w-4" /> Agregar ítem
            </Button>
          </div>

          <div>
            <Label>Notas (opcional)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} className="gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Solicitar transferencia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
